import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * On every route change, jump the window to the top of the page.
 * Instant (no smooth scroll) so tapping a nav item or article link feels
 * like landing on a fresh screen instead of dumping the user mid-scroll.
 *
 * Uses history "action" awareness: on POP (back/forward) we let the browser
 * restore the previous scroll position — Chrome/Safari do this natively.
 */
export default function ScrollToTop() {
  const { pathname, search } = useLocation();

  // Disable the browser's default scroll restoration ONCE — otherwise
  // Chromium/Safari can quietly re-apply the previous scroll position AFTER
  // our scrollTo(0,0) fires, making it look like the page never reset.
  useEffect(() => {
    if (typeof window === "undefined" || !window.history) return;
    try { window.history.scrollRestoration = "manual"; } catch { /* noop */ }
  }, []);

  useEffect(() => {
    // Some routes encode tab state in ?tab= — treat those the same as a
    // fresh navigation so switching between /roster?tab=depth and
    // /roster?tab=cap also snaps to top.
    try {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    } catch {
      // Older Safari doesn't accept "instant" — fall back to auto.
      window.scrollTo(0, 0);
    }
    // Follow-up on next frame in case a lazy-loaded layout expands after
    // the first scroll (e.g. hero image resolves, sidebar hydrates) and
    // pushes the viewport back down. Cheap and safe.
    requestAnimationFrame(() => {
      try { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); } catch { window.scrollTo(0, 0); }
    });
  }, [pathname, search]);

  return null;
}
